import { Fragment } from "react";
import { flexRender } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import type { Row, Table } from "~/shared/react-table/app-table";
import { TableCell, TableRow, SkeletonRows } from "~/components/table";
import { SearchProductWithScore } from "~/hooks/search/types";
import { BooksTableDetailRow } from "./books-table-detail-row";

export interface BooksTableRowData {
  nonExistent?: boolean;
  workId?: string | null;
}

export const BooksTableBody = <TData extends BooksTableRowData>({
  table,
  isLoading,
  hasDetails,
  onRowClick,
}: {
  table: Table<TData>;
  onRowClick: (product: TData, rowIndex: number) => void;
  hasDetails?: boolean;
  isLoading: boolean;
}) => {
  const { t } = useTranslation(["components"]);
  const columns = table.getVisibleLeafColumns();

  if (isLoading) {
    return <SkeletonRows columns={columns} />;
  }

  const { rows } = table.getRowModel();

  if (!rows.length) {
    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center">
          {t("components:booksTable.noResults.label")}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {rows.map((row, index) => {
        // Semantic search lists every edition of a work as consecutive rows;
        // the markers expose the group boundaries without changing the layout.
        const workId = row.original.workId ?? undefined;
        const isWorkGroupStart =
          !!workId &&
          (index === 0 || workId !== rows[index - 1].original.workId);

        return (
          <Fragment key={row.id}>
            <TableRow
              data-testid={`book-${row.id}`}
              data-book-id={row.id}
              data-work-id={workId}
              data-work-group-start={isWorkGroupStart || undefined}
              data-state={row.getIsSelected() && "selected"}
              className={
                hasDetails
                  ? "group cursor-pointer border-b border-secondary-50 text-secondary-600 hover:bg-transparent data-[state=selected]:bg-secondary-25"
                  : ""
              }
              onClick={() => {
                if (row.original.nonExistent) {
                  return;
                }

                onRowClick(row.original, row.index);
                if (hasDetails) {
                  row.toggleExpanded();
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  id={cell.id}
                  columnDef={cell.column.columnDef}
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
            {hasDetails && row.getIsExpanded() && (
              // Detail rows are only used by the hybrid books table, whose
              // rows are always SearchProductWithScore.
              <BooksTableDetailRow
                product={row.original as unknown as SearchProductWithScore}
                colSpan={row.getVisibleCells().length}
                row={row as unknown as Row<SearchProductWithScore>}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};
